import { movies } from '@/mocks/movies';
import { tvShows } from '@/mocks/tvshows';
import type { MediaTitle } from '@/types';

export const allTitles: MediaTitle[] = [
  ...(movies as unknown as MediaTitle[]),
  ...(tvShows as unknown as MediaTitle[]),
];

const byId = new Map<string, MediaTitle>(allTitles.map((item) => [item.id, item]));

export function getTitleById(id?: string): MediaTitle | undefined {
  if (!id) return undefined;
  return byId.get(id);
}

export function getByType(type: 'movie' | 'tv'): MediaTitle[] {
  return allTitles.filter((item) => item.type === type);
}

export function getFeatured(limit = 5): MediaTitle[] {
  const featured = allTitles.filter((item) => item.featured);
  const pool = featured.length >= 3 ? featured : allTitles.slice().sort((a, b) => b.views - a.views);
  return pool.slice(0, limit);
}

export function getLatest(type: 'movie' | 'tv', limit = 6): MediaTitle[] {
  return getByType(type)
    .slice()
    .sort((a, b) => b.year - a.year || b.views - a.views)
    .slice(0, limit);
}

export function getTopRated(limit = 6, type?: 'movie' | 'tv'): MediaTitle[] {
  const pool = type ? getByType(type) : allTitles;
  return pool
    .slice()
    .sort((a, b) => b.rating - a.rating)
    .slice(0, limit);
}

export function getMostViewed(limit = 6, type?: 'movie' | 'tv'): MediaTitle[] {
  const pool = type ? getByType(type) : allTitles;
  return pool
    .slice()
    .sort((a, b) => b.views - a.views)
    .slice(0, limit);
}

export function getUniqueValues(items: MediaTitle[], key: 'region' | 'year' | 'genres'): string[] {
  const set = new Set<string>();
  items.forEach((item) => {
    if (key === 'genres') {
      item.genres.forEach((g) => set.add(g));
    } else {
      set.add(String(item[key]));
    }
  });
  return Array.from(set);
}

export function getGenres(type?: 'movie' | 'tv'): string[] {
  return getUniqueValues(type ? getByType(type) : allTitles, 'genres');
}

export function getRegions(type?: 'movie' | 'tv'): string[] {
  return getUniqueValues(type ? getByType(type) : allTitles, 'region').sort((a, b) => a.localeCompare(b, 'zh'));
}

export function getYears(type?: 'movie' | 'tv'): string[] {
  return getUniqueValues(type ? getByType(type) : allTitles, 'year')
    .map(Number)
    .sort((a, b) => b - a)
    .map(String);
}

export interface FilterParams {
  type?: 'movie' | 'tv';
  genre?: string;
  year?: string;
  region?: string;
  sort?: string;
}

export function filterTitles({
  type,
  genre = 'all',
  year = 'all',
  region = 'all',
  sort = 'latest',
}: FilterParams): MediaTitle[] {
  let list = type ? getByType(type) : allTitles.slice();

  if (genre && genre !== 'all') {
    list = list.filter((item) => item.genres.includes(genre));
  }
  if (year && year !== 'all') {
    list = list.filter((item) => String(item.year) === year);
  }
  if (region && region !== 'all') {
    list = list.filter((item) => item.region === region);
  }

  switch (sort) {
    case 'hot':
      list.sort((a, b) => b.views - a.views);
      break;
    case 'rating':
      list.sort((a, b) => b.rating - a.rating);
      break;
    default:
      list.sort((a, b) => b.year - a.year || b.views - a.views);
  }

  return list;
}

export function searchTitles(keyword: string): MediaTitle[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allTitles.filter((item) => {
    const haystack = [
      item.title,
      item.summary,
      item.region,
      item.director,
      ...item.genres,
      ...item.cast,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelated(title: MediaTitle, limit = 6): MediaTitle[] {
  const scored = allTitles
    .filter((item) => item.id !== title.id)
    .map((item) => {
      let score = 0;
      if (item.type === title.type) score += 2;
      score += item.genres.filter((g) => title.genres.includes(g)).length * 3;
      if (item.director === title.director) score += 4;
      if (item.region === title.region) score += 1;
      score += item.rating / 10;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((s) => s.item);
}

export const SORT_OPTIONS = [
  { value: 'latest', label: '最新' },
  { value: 'hot', label: '热门' },
  { value: 'rating', label: '评分' },
];

export const PAGE_SIZE = 8;

export function paginate<T>(list: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}